/**
 * Studio V5 Types
 *
 * Extended profile fields for the Studio tab.
 * New fields are additive — they don't exist on UserProfile yet
 * and are written via updateProfile({ merge: true }).
 *
 * Backward compatibility:
 *   profile.monthlyBurn    (cents)  → studioProfile.monthlyBurn (dollars)
 *   profile.monthlyCapacity (hours) → studioProfile.monthlyCapacity (hours)
 *   profile.careerStage             → studioProfile.careerStage
 *   profile.pdfBrandName            → studioProfile.displayName
 */

import type { CareerStageKey } from '@/types';

// ─── Studio Profile (superset of relevant UserProfile fields) ───

export interface StudioProfile {
  // Identity
  displayName: string;
  practiceName: string;
  avatarUrl: string | null;

  // Economics (dollars, not cents — converted at read/write boundary)
  monthlyBurn: number;
  monthlyCapacity: number;
  careerStage: CareerStageKey | '';
  burnSource: 'plaid' | 'manual' | '';
  burnCalculatedAt: string | null;
  highestSale: number | null;
  location: string;

  // Contact
  contactEmail: string;
  contactPhone: string;
  website: string;

  // Quote defaults

  // Rights
  defaultCopyrightRetained: true;
  defaultPortfolioUse: boolean;
  defaultCancellationPolicy: 'standard' | 'store_credit';

  // Theme
  accentColor: string;
}

// ─── Defaults ───

export const STUDIO_DEFAULTS: StudioProfile = {
  displayName: '',
  practiceName: '',
  avatarUrl: null,
  monthlyBurn: 3000,
  monthlyCapacity: 130,
  careerStage: '',
  burnSource: '',
  burnCalculatedAt: null,
  highestSale: null,
  location: '',
  contactEmail: '',
  contactPhone: '',
  website: '',
  defaultCopyrightRetained: true,
  defaultPortfolioUse: true,
  defaultCancellationPolicy: 'standard',
  accentColor: '#3DABA5',
};

// ─── Read from UserProfile (Firestore → local state) ───

export function readStudioProfile(profile: any): StudioProfile {
  if (!profile) return { ...STUDIO_DEFAULTS };

  return {
    displayName: profile.pdfBrandName || profile.displayName || '',
    practiceName: profile.practiceName || '',
    avatarUrl: profile.avatarUrl || null,
    monthlyBurn: profile.monthlyBurn ? profile.monthlyBurn / 100 : STUDIO_DEFAULTS.monthlyBurn,
    monthlyCapacity: profile.monthlyCapacity || STUDIO_DEFAULTS.monthlyCapacity,
    careerStage: profile.careerStage || '',
    burnSource: profile.burnSource || '',
    burnCalculatedAt: profile.burnCalculatedAt || null,
    highestSale: profile.highestSale ? profile.highestSale / 100 : null,
    location: profile.location || '',
    contactEmail: profile.contactEmail || profile.email || '',
    contactPhone: profile.contactPhone || '',
    website: profile.website || '',
    defaultCopyrightRetained: true,
    defaultPortfolioUse: profile.defaultPortfolioUse ?? true,
    defaultCancellationPolicy: profile.defaultCancellationPolicy ?? 'standard',
    accentColor: profile.accentColor ?? '#3DABA5',
  };
}

// ─── Write to Firestore (local state → profile update payload) ───

export function toProfileUpdate(sp: StudioProfile): Record<string, any> {
  return {
    // Backward compat: write to existing fields
    pdfBrandName: sp.displayName,
    monthlyBurn: Math.round(sp.monthlyBurn * 100), // dollars → cents
    monthlyCapacity: sp.monthlyCapacity,
    careerStage: sp.careerStage || 'emerging',
    burnSource: sp.burnSource || 'manual',
    location: sp.location || null,
    highestSale: sp.highestSale ? Math.round(sp.highestSale * 100) : null,

    // New V5 fields (additive, merge-safe)
    practiceName: sp.practiceName,
    avatarUrl: sp.avatarUrl,
    contactEmail: sp.contactEmail,
    contactPhone: sp.contactPhone,
    website: sp.website,
    defaultCopyrightRetained: true,
    defaultPortfolioUse: sp.defaultPortfolioUse,
    defaultCancellationPolicy: sp.defaultCancellationPolicy,
    accentColor: sp.accentColor,
  };
}

// ─── Delivery / Color lookup tables ───


export const ACCENT_COLORS = [
  { hex: '#3DABA5', name: 'Teal' },
  { hex: '#2C3E6B', name: 'Navy' },
  { hex: '#8B3A4A', name: 'Burgundy' },
  { hex: '#6B8F6B', name: 'Sage' },
  { hex: '#B8963E', name: 'Gold' },
] as const;

export const CAREER_OPTIONS = [
  { value: 'unlisted' as const, label: 'Unlisted', desc: 'Not actively selling yet' },
  { value: 'emerging' as const, label: 'Emerging Artist', desc: 'Early sales, building a practice' },
  { value: 'working' as const, label: 'Working Artist', desc: 'Regular sales, pricing pressure' },
  { value: 'established' as const, label: 'Established', desc: 'Proven market, pricing leverage' },
];
